import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, SessionInformationService, UtilityService } from 'app/core/core.module';
import { Dealer } from 'app/core/model/dealer';
import { Photo } from 'app/core/model/entity-model';
import { OrderLineDetail } from 'app/core/model/order-line-detail';
import { PackingSlip } from 'app/core/model/packing-slip';
import { PictureService } from 'app/core/picture.service';
import { Utility } from 'app/core/utility';
import { alert } from 'devextreme/ui/dialog';
import _ from 'lodash';

@Component({
    selector: 'app-pictures-entry',
    templateUrl: './pictures-entry.component.html',
    styleUrls: ['./pictures-entry.component.scss'],
    standalone: false
})
export class PicturesEntryComponent implements OnInit {
    mode: 'Invoice' | 'PackingSlip' | 'SalesOrder';
    filter: any;
    subItems: any[];
    photos: Photo[];
    dealers: Dealer[];
    packSlip: PackingSlip;
    orderLineDetails: OrderLineDetail[];
    canAddPicture = false;

    constructor(
        private uow: GenesisUnitOfWork,
        private sessionInfo: SessionInformationService,
        private appInfo: AppInformationService,
        private pictureService: PictureService,
        private utility: UtilityService,
        private auth: AuthService,
        private route: ActivatedRoute
    ) {}

    async ngOnInit() {
        this.dealers = await this.uow.getDealers();

        this.route.data.subscribe(async (data) => {
            this.mode = this.route.snapshot.data['mode'];
            this.filter = this.sessionInfo.getItem(`${this.mode}-pictures-filter`) ?? { dealerId: null, subItemId: null };
            if (this.filter.dealerId) await this.getSubItems();
            if (this.filter.subItemId) await this.getPhotos();
        });
    }

    async onDealerChanges(e) {
        this.filter.subItemId = null;
        await this.getSubItems();
        this.sessionInfo.setItem(`${this.mode}-pictures-filter`, this.filter);
    }

    onSubItemChange = async (e) => {
        await this.getPhotos();
        this.sessionInfo.setItem(`${this.mode}-pictures-filter`, this.filter);
    };

    async search() {
        if (!this.filter.packingSlipNumber) return;

        this.appInfo.pleaseWaitShow();
        this.packSlip = await this.uow.getPackSlipByNumber(this.filter.packingSlipNumber, 'orderLineDetails.orderLine');
        this.canAddPicture = !!this.packSlip;
        if (!this.packSlip) {
            await alert(`Could not find packing slip for slip #${this.filter.packingSlipNumber}`, this.appInfo.settings.company);
            this.appInfo.pleaseWaitHide();
            return;
        }
        const details = this.packSlip.orderLineDetails.filter((line) => !line.deleted);
        this.orderLineDetails = _.sortBy(details, [this.groupId, 'id']);
        switch (this.mode) {
            case 'PackingSlip':
                this.photos = await this.uow.getPhotos(this.packSlip.id, 'Pack Slip');
                break;
        }
        console.log(this.packSlip);
        this.photos = this.photos.filter((photo) => Utility.isImage(photo.filename));
        this.photos.forEach((photo) => (photo.userCanDelete = photo.createdBy === this.auth.currentUser.userName || this.auth.hasRole('Pictures:Admin')));
        this.sessionInfo.setItem(`${this.mode}-pictures-filter`, this.filter);
        this.appInfo.pleaseWaitHide();
    }

    async getSubItems() {
        switch (this.mode) {
            case 'Invoice':
            case 'PackingSlip':
                this.subItems = await this.uow.getPackSlipByDealer(this.filter.dealerId);
                break;
            case 'SalesOrder':
                this.subItems = await this.uow.getOrdersByDealer(this.filter.dealerId);
                break;
        }
    }

    async takePicture(choose: boolean = false) {
        if (!this.filter.subItemId && !this.packSlip) return;

        const imageData = await this.pictureService.getImage(choose);
        if (!imageData) return;

        this.appInfo.pleaseWaitShow();

        let entityId: number;
        switch (this.mode) {
            case 'Invoice':
                entityId = this.filter.subItemId;
                break;
            case 'PackingSlip':
                entityId = this.packSlip.id;
                break;
            case 'SalesOrder':
                entityId = this.filter.subItemId;
                break;
        }

        const result = await this.utility.uploadImageData(entityId, imageData, this.mode);
        if (result.success) await this.getPhotos();
        this.appInfo.pleaseWaitHide();
    }

    clear() {
        this.filter = { dealerId: null, subItemId: null };
        this.packSlip = null;
        this.orderLineDetails = null;
        this.sessionInfo.removeItem(`${this.mode}-pictures-filter`);
    }

    groupId(data: OrderLineDetail): number {
        return data.parentLineId ?? data.id;
    }

    isGroupStart(data: OrderLineDetail, index: number): boolean {
        if (index === 0) return false;
        return this.groupId(data) !== this.groupId(this.orderLineDetails[index - 1]);
    }

    async getPhotos() {
        this.photos = [];

        switch (this.mode) {
            case 'Invoice':
                if (!this.filter?.dealerId || !this.filter?.subItemId) {
                    return;
                }
                this.canAddPicture = true;
                this.photos = await this.uow.getPhotos(this.filter.subItemId, 'Pack Slip');
                break;
            case 'PackingSlip':
                this.photos = await this.uow.getPhotos(this.packSlip.id, 'Pack Slip');
                break;
            case 'SalesOrder':
                if (!this.filter?.dealerId || !this.filter?.subItemId) {
                    return;
                }
                this.canAddPicture = true;
                this.photos = await this.uow.getPhotos(this.filter.subItemId, 'Order');
                break;
        }

        this.photos = this.photos.filter((photo) => Utility.isImage(photo.filename));
        this.photos.forEach((photo) => (photo.userCanDelete = photo.createdBy === this.auth.currentUser.userName || this.auth.hasRole('Pictures:Admin')));
    }

    async removePhoto(e: any, photo: Photo) {
        e.preventDefault();
        photo.deleted = new Date();
        photo.deletedBy = this.auth.currentUser.userName;
        this.photos.splice(this.photos.indexOf(photo), 1);
        await this.uow.commit([photo]);
    }

    openPhoto(photo: Photo) {
        window.open(photo.filename, '_blank');
    }
}
